import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { InfoBanner } from "@/components/ui/banner";
import { toast } from "@/components/ui/toast";
import { localOnly } from "@/lib/fixture-notice";
import { cn } from "@/lib/utils";
import { usePermission } from "@/hooks/use-permission";
import type {
  TransferApprover,
  TransferDetail,
  TransferDetailState,
  TransferEventTone,
  TransferTimelineEvent,
} from "@/types";

const CROSS_MODULE_HINT = "Available once the linked module is built";

const DOT_TONE: Record<TransferEventTone, string> = {
  success: "var(--ads-success)",
  active: "var(--ads-primary)",
  danger: "var(--ads-danger)",
  pending: "var(--ads-line-soft)",
};

const TITLE_TONE: Record<TransferEventTone, string> = {
  success: "text-ink",
  active: "text-primary",
  danger: "text-danger",
  pending: "text-muted",
};

function DisabledLink({ children, title }: { children: React.ReactNode; title: string }) {
  return (
    <span aria-disabled title={title} className="font-semibold text-primary">
      {children}
    </span>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-6">
      <h2 className="mb-3 text-[15px] font-bold text-ink">{title}</h2>
      {children}
    </section>
  );
}

function KVPanel({ rows }: { rows: { k: string; v: React.ReactNode }[] }) {
  return (
    <div className="overflow-hidden rounded-[10px] border border-line-soft bg-surface">
      {rows.map((row, i) => (
        <div
          key={`${row.k}-${i}`}
          className={cn("flex items-start justify-between gap-4 px-5 py-3.5", i < rows.length - 1 && "border-b border-line-soft")}
        >
          <span className="shrink-0 pt-0.5 text-[12px] text-muted">{row.k}</span>
          <span className="max-w-[62%] break-words text-right text-[12.5px] font-medium text-ink">{row.v}</span>
        </div>
      ))}
    </div>
  );
}

function Timeline({ events }: { events: TransferTimelineEvent[] }) {
  return (
    <div className="overflow-hidden rounded-lg border border-line bg-surface px-5">
      {events.map((e, i) => (
        <div
          key={`${e.title}-${i}`}
          className={cn("grid grid-cols-[14px_1fr] gap-4 py-3.5", i < events.length - 1 && "border-b border-line")}
        >
          <span aria-hidden className="mt-1 size-3.5 shrink-0 rounded-[7px]" style={{ background: DOT_TONE[e.tone] }} />
          <div>
            <div className={cn("text-[13px] font-semibold", TITLE_TONE[e.tone])}>{e.title}</div>
            {e.meta && <div className="mt-0.5 text-[11.5px] text-muted">{e.meta}</div>}
            {e.detail && <div className="mt-1 text-[11.5px] leading-relaxed text-muted">{e.detail}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function SegBar({ total, done }: { total: number; done: number }) {
  return (
    <div className="flex gap-1" style={{ width: total * 146 + (total - 1) * 4 }}>
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          aria-hidden
          className="h-2 rounded-[4px]"
          style={{ width: 146, background: i < done ? "var(--ads-viz-amber-soft)" : "var(--ads-disabled-soft)" }}
        />
      ))}
    </div>
  );
}

function ApproverList({ approvers }: { approvers: TransferApprover[] }) {
  return (
    <div className="flex flex-col gap-2">
      {approvers.map((a) => (
        <div key={a.name} className="flex items-center gap-3 rounded-lg border border-line bg-surface px-3.5 py-2.5">
          <span className="grid size-8 shrink-0 place-items-center rounded-full bg-primary text-xs font-bold text-white">
            {a.initials}
          </span>
          <div>
            <div className="text-[13px] font-bold text-ink">{a.name}</div>
            <div className="text-[11.5px] text-muted">{a.role}</div>
          </div>
          <Badge variant={a.statusVariant} dot={false} size="sm" className="ml-auto">
            {a.statusLabel}
          </Badge>
        </div>
      ))}
    </div>
  );
}

function GreenPanel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-tint-success-border bg-tint-success px-4 py-3">
      <div className="text-[13px] font-semibold text-success-strong">{title}</div>
      <p className="mt-1 text-[11.5px] leading-relaxed text-text-strong">{children}</p>
    </div>
  );
}

function AmberPanel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-tint-warning-border bg-warning-soft px-4 py-3">
      <div className="text-[13px] font-semibold text-warning">{title}</div>
      <p className="mt-1 text-[11.5px] leading-relaxed text-text-strong">{children}</p>
    </div>
  );
}

/* Source/destination strings look like "HT Operations Hot (WLT-001)" for
   internal wallets or "0x8f2e…3a4b (external)" for external addresses — only
   the former has a real destination route (/wallets/[id]) to link to. */
function WalletRefText({ text }: { text: string }) {
  const match = text.match(/\(WLT-\d+\)/);
  if (!match) return <>{text}</>;
  const walletId = match[0].slice(1, -1);
  return (
    <Link to={`/wallets/${walletId}`} className="font-semibold text-primary hover:underline">
      {text}
    </Link>
  );
}

function amountNode(detail: TransferDetail) {
  return (
    <span className="inline-flex items-baseline gap-2">
      <span className="text-[15px] font-bold text-ink">{detail.amountMain}</span>
      <span className="text-xs font-medium text-muted">{detail.amountUsd}</span>
    </span>
  );
}

export default function TransferDetailView({ detail, state }: { detail: TransferDetail; state: TransferDetailState }) {
  const id = detail.id;
  const canApprove = usePermission("approval:approve");
  const canReject = usePermission("approval:reject");
  const canEscalate = usePermission("transfer:escalate");

  const kvRows: { k: string; v: React.ReactNode }[] = [{ k: "Intent ID", v: <span className="font-mono">{id}</span> }, { k: "Amount", v: amountNode(detail) }];

  if (state === "rejected") {
    kvRows.push(
      { k: "Status", v: <Badge variant="danger" dot={false} size="sm">Rejected — Terminal</Badge> },
      { k: "Created By", v: detail.createdBy },
      { k: "Rejected By", v: "Marcus Webb (USR-005) · 09:30 UTC" },
    );
  } else if (state === "failed") {
    kvRows.push(
      { k: "Failure Point", v: detail.failure?.failurePoint ?? "—" },
      { k: "Error", v: <span className="font-mono text-[11.5px]">{detail.failure?.error ?? "—"}</span> },
      { k: "Created By", v: detail.createdBy },
    );
  } else if (state === "reconciled" && detail.finality) {
    kvRows.push(
      { k: "Direction", v: detail.direction === "receive" ? "↓ Received (inbound)" : "↑ Sent (outbound)" },
      { k: "From", v: <WalletRefText text={detail.source} /> },
      { k: "To", v: <WalletRefText text={detail.destination} /> },
      { k: "Provider Route", v: detail.providerRoute },
    );
  } else if (state === "pending-approval") {
    kvRows.push(
      { k: "Created By", v: detail.createdBy },
      { k: "Policy", v: detail.policy ?? "—" },
    );
  } else if (state === "pending-policy") {
    kvRows.push({ k: "Created By", v: detail.createdBy });
  } else if (state === "approved") {
    kvRows.push({ k: "Provider Route", v: detail.providerRoute });
  } else {
    kvRows.push(
      { k: "Source", v: <WalletRefText text={detail.source} /> },
      { k: "Destination", v: <WalletRefText text={detail.destination} /> },
      { k: "Created By", v: detail.createdBy },
      { k: "Provider Route", v: detail.providerRoute },
    );
  }

  return (
    <div className="max-w-[760px]">
      <Breadcrumb items={[{ label: "Transfers", href: "/transfers" }, { label: id }]} />

      {state === "draft" && (
        <InfoBanner variant="info" className="mb-4">
          Draft — this intent has not been submitted. Policy evaluation and screening run on submit.
        </InfoBanner>
      )}

      {state === "failed" && detail.failure && (
        <div className="mb-4 rounded-[8px] bg-tint-danger px-4 py-3 text-[12.5px] font-medium text-danger">
          {detail.failure.banner}
        </div>
      )}

      {state === "outcome-unknown" && (
        <InfoBanner variant="warning" className="mb-4" title="Outcome unknown">
          Broadcast status is indeterminate. Do not retry until the provider confirms the on-chain state.
        </InfoBanner>
      )}

      <KVPanel rows={kvRows} />

      {state === "pending-policy" && detail.screening && (
        <Section title="Screening Status">
          <div className="flex flex-col gap-2.5">
            <GreenPanel title={`✓ ${detail.screening.passedTitle}`}>{detail.screening.passedText}</GreenPanel>
            <AmberPanel title={`⏳ ${detail.screening.holdTitle}`}>{detail.screening.holdText}</AmberPanel>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => toast({ variant: "info", title: "Screening status refreshed", description: localOnly("Chainalysis assessment still in progress.") })}
            >
              Check Status
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => toast({ variant: "warning", title: "Cancel requested", description: localOnly(`${id} will be cancelled once screening returns.`) })}
            >
              Cancel Intent
            </Button>
          </div>
        </Section>
      )}

      {state === "pending-approval" && detail.quorum && (
        <Section title={detail.quorum.label}>
          <p className="mb-3 text-[12.5px] text-muted">
            Required: {detail.quorum.needed} of {detail.quorum.total} approvers · SLA: 4h (expires 14:42 UTC)
          </p>
          <div className="mb-4">
            <SegBar total={detail.quorum.total} done={detail.quorum.done} />
          </div>
          <ApproverList approvers={detail.quorum.approvers} />
          <div className="mt-4 flex flex-wrap gap-2">
            {canApprove && (
              <Link to={`/approvals/${id}`} className={buttonVariants({ size: "sm" })}>
                Approve
              </Link>
            )}
            {canReject && (
              <Link to={`/approvals/${id}`} className={buttonVariants({ variant: "destructive", size: "sm" })}>
                Reject
              </Link>
            )}
            {canEscalate && (
              <Link to={`/approvals/${id}/escalate`} className={buttonVariants({ variant: "secondary", size: "sm" })}>
                Escalate
              </Link>
            )}
          </div>
        </Section>
      )}

      {state === "approved" && detail.signing && (
        <Section title={detail.signing.heading}>
          <Timeline events={detail.signing.steps} />
          <InfoBanner variant="info" className="mt-3 mb-0">
            {detail.signing.note}
          </InfoBanner>
        </Section>
      )}

      {state === "reconciled" && detail.finality && (
        <>
          <Section title="Finality">
            <KVPanel
              rows={[
                { k: "Block", v: detail.finality.block },
                { k: "Confirmations", v: `${detail.finality.confirmations}` },
                { k: "Tx Hash", v: <DisabledLink title={CROSS_MODULE_HINT}>{detail.finality.txHash} · Etherscan ↗</DisabledLink> },
                { k: "Gas Used", v: detail.finality.gasUsed },
              ]}
            />
          </Section>
          {detail.reconciliation && (
            <Section title="Reconciliation">
              <GreenPanel title="✓ Reconciled">{detail.reconciliation}</GreenPanel>
            </Section>
          )}
        </>
      )}

      {state === "rejected" && detail.rejection && (
        <>
          <Section title="Rejection Details">
            <div className="rounded-lg border border-line bg-surface px-4 py-3.5">
              <div className="text-[13px] font-semibold text-ink">Reason: {detail.rejection.reason}</div>
              <blockquote className="mt-2 border-l-2 border-line pl-3 text-[12.5px] italic leading-relaxed text-text">
                “{detail.rejection.quote}”
              </blockquote>
              <div className="mt-2 text-[11.5px] text-muted">— {detail.rejection.by}</div>
            </div>
          </Section>
          {detail.evidenceReceipt && (
            <Section title="Evidence Receipt">
              <KVPanel
                rows={[
                  { k: "Evidence ID", v: <Link to={`/evidence/${detail.evidenceReceipt.evidenceId}`} className="font-mono text-primary hover:underline">{detail.evidenceReceipt.evidenceId}</Link> },
                  { k: "Disposition", v: detail.evidenceReceipt.disposition },
                  { k: "Audit Trail", v: <DisabledLink title={CROSS_MODULE_HINT}>View in admin audit →</DisabledLink> },
                ]}
              />
            </Section>
          )}
        </>
      )}

      {state === "failed" && detail.failure && (
        <Section title="Recovery Options">
          <div className="flex flex-col gap-2">
            {detail.failure.options.map((o) => (
              <div
                key={o.title}
                className={cn(
                  "flex flex-wrap items-center gap-3 rounded-lg border bg-surface px-4 py-3.5",
                  o.primary ? "border-2 border-primary" : "border-line"
                )}
              >
                <div className="min-w-0">
                  <div className="text-[13px] font-bold text-ink">{o.title}</div>
                  <div className="mt-0.5 text-[12px] text-muted">{o.description}</div>
                </div>
                <div className="ml-auto shrink-0">
                  {o.action === "Clone Intent" ? (
                    <Link to="/transfers/new" className={buttonVariants({ variant: "secondary", size: "sm" })}>
                      {o.action}
                    </Link>
                  ) : (
                    <Button
                      variant={o.variant}
                      size="sm"
                      disabled={o.action === "Escalate" && !canEscalate}
                      onClick={() => {
                        if (o.action === "Escalate" && !canEscalate) return;
                        toast(
                          o.variant === "primary"
                            ? { variant: "info", title: "Retry queued", description: localOnly("A fresh broadcast attempt will be made.") }
                            : o.variant === "destructive"
                              ? { variant: "warning", title: "Intent cancelled", description: localOnly(`${id} marked as terminal failure.`) }
                              : { variant: "info", title: "Escalated", description: localOnly("Provider support ticket will be created.") }
                        );
                      }}
                    >
                      {o.action}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {state === "outcome-unknown" && (
        <Section title="Recovery Options">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => toast({ variant: "info", title: "Status check requested", description: localOnly("Querying provider for on-chain state.") })}
            >
              Check Status
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!canEscalate}
              onClick={() => canEscalate && toast({ variant: "info", title: "Escalated", description: localOnly("Provider support ticket will be created.") })}
            >
              Escalate
            </Button>
          </div>
        </Section>
      )}

      {(state === "draft" || state === "pending-policy" || state === "broadcast-confirmed" || state === "reconciled" || state === "outcome-unknown") && (
        <Section title={state === "draft" ? "Draft Timeline" : "Transfer Timeline"}>
          <Timeline events={detail.timeline} />
        </Section>
      )}

      {state === "failed" && detail.failure && (
        <Section title="Failure Timeline">
          <Timeline events={detail.timeline} />
        </Section>
      )}

      {state === "draft" && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/transfers/new" className={buttonVariants({ size: "sm" })}>
            Edit Draft
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => toast({ variant: "warning", title: "Draft discarded", description: localOnly(`${id} was removed.`) })}
          >
            Discard
          </Button>
        </div>
      )}

      {detail.evidence && state !== "rejected" && (
        <Section title="Evidence">
          <div className="flex flex-col gap-2">
            {detail.evidence.map((e) => (
              <div key={e.id} className="text-[13px]">
                <Link to={`/evidence/${e.id}`} className="font-semibold text-primary hover:underline">
                  {e.id} — {e.label}
                </Link>
              </div>
            ))}
          </div>
        </Section>
      )}

      <div className="mt-7">
        <Link to="/transfers" className={buttonVariants({ variant: "secondary", size: "sm" })}>
          ← Back to Transfers
        </Link>
      </div>
    </div>
  );
}
